<template>
  <lm-modal
    :ref="modalName"
    :name="modalName"
    class="ModalUserList"
    title="User List"
    full-height
    disable-tabindex
    :remove-footer="!hasAdminRights"
    @[Events.AFTER_OPEN]="modalOpened"
  >
    <template slot="error">
      <lm-alert
        v-show="alert.show"
        :msg="alert.message"
        :type="alert.type"
        @close="alert.show = false"
      />
    </template>
    <template v-if="users.length > 0">
      <div v-if="hasAdminRights" class="Users PendingUsers">
        <header class="Users-header">
          <h3 class="Users-title">
            {{ invitedUsers.length }} pending invitations
          </h3>
        </header>
        <ul v-if="invitedUsers.length > 0" class="Users-list">
          <invited-user-item
            v-for="invitation in invitedUsers"
            :key="invitation.email"
            :invitation="invitation"
            @updateRole="updateRoleInvitation"
            @revoke="onRevoke"
            @send="onReSend"
            @error="onError"
          />
        </ul>
      </div>
      <div class="Users">
        <header class="Users-header">
          <h3 class="Users-title">{{ sortedUsers.length }} users</h3>
          <lm-search
            v-model="search"
            class="SearchUsers"
            :placeholder="searchPlaceholderText"
            @close="search = ''"
          />
        </header>
        <lm-users-table :options="options" @change-order="onChange">
          <table-user-item
            v-for="user in sortedUsers"
            :key="user._id"
            :user="user"
            @delete="onDeleteUser"
            @error="onError"
          />
        </lm-users-table>
      </div>
      <template v-if="hasAdminRights" slot="footer">
        <invitation-form @error="onError" @update="updateInvitedUser" />
      </template>
    </template>
  </lm-modal>
</template>

<script>
import LmAlert from '@/components/ui/LmAlert'
import {orderBy} from 'lodash'
import {mapGetters} from 'vuex'
import {
  Events,
  Modal,
  ErrorCodes,
  AlertMessages,
  Roles
} from '@/lib/definitions'
import InvitationForm from './UserList/InvitationForm'
import InvitedUserItem from './UserList/InvitedUserItem'
import LmModal from '@/components/ui/LmModal'
import LmSearch from '@/components/ui/LmSearch'
import LmUsersTable from '@/components/ui/LmUsersTable'
import TableUserItem from '@/components/ui/usersTable/UserItem'

const getRolesSortPriority = role => {
  switch (role) {
    case Roles.OWNER:
      return 1
    case Roles.ADMIN:
      return 2
    case Roles.MANAGER:
      return 3
    default:
      return 4
  }
}

export default {
  name: 'ModalUserList',
  components: {
    LmAlert,
    TableUserItem,
    LmUsersTable,
    LmSearch,
    LmModal,
    InvitedUserItem,
    InvitationForm
  },
  data() {
    return {
      Events,
      modalName: Modal.USER_LIST,
      alert: {
        type: 'error',
        show: false,
        message: ''
      },
      users: [],
      invitedUsers: [],
      search: '',
      orderCriteria: 'role',
      orderBy: 'asc',
      timeout: null
    }
  },
  computed: {
    ...mapGetters([
      'hasAdminRights',
      'currentLandscapeUsers',
      'currentLandscapeActiveUsers',
      'currentLandscape'
    ]),
    options() {
      return [
        {title: 'Name', id: 'username'},
        {
          title: 'Role',
          id: 'role',
          defaultOrder: true,
          help: 'role'
        }
      ]
    },
    filteredUsers() {
      let fUsers = this.users
      if (this.search) {
        let str = this.search.toLowerCase()
        fUsers = fUsers.filter(user => {
          return (
            user.username.toLowerCase().indexOf(str) > -1 ||
            (user.email && user.email.toLowerCase().indexOf(str) > -1)
          )
        })
      }
      return fUsers
    },
    sortedUsers() {
      const order =
        this.orderCriteria === 'role'
          ? [user => getRolesSortPriority(user.role)]
          : this.orderCriteria
      return orderBy(this.filteredUsers, order, this.orderBy)
    }
  },
  watch: {
    $route(to, from) {
      if (from.query && from.query.submodal === Modal.MANAGER) {
        this.loadUsers() // Not optimal !?
      }
    }
  },
  created() {
    this.searchPlaceholderText = 'Search by name'
    if (this.hasAdminRights) this.searchPlaceholderText += ' or email'
    if (this.currentLandscapeUsers.length > 0) this.loadUsers() // UX
  },
  methods: {
    loadUsers() {
      this.$store
        .dispatch('loadUsers')
        .then(res => {
          this.setUsers()
          this.invitedUsers = res.invitedUsers
        })
        .catch(e => {
          this.showAlert(`Error ${e.devCode}: ${e.devMessage}`)
        })
    },
    setUsers() {
      if (this.hasAdminRights) {
        // Do not list eliminated users, but list downgraded
        // (downgraded are eliminated by design)
        this.users = this.currentLandscapeUsers.filter(
          user => !user.ex || (user.ex && user.downgrade)
        )
      } else {
        this.users = this.currentLandscapeActiveUsers
      }
    },
    onChange(column) {
      this.orderCriteria = column.id
      this.orderBy = column.orderASC ? 'asc' : 'desc'
    },
    modalOpened() {
      // Refresh browser case
      if (!this.currentLandscape) setTimeout(this.modalOpened, 500)
      else if (this.users.length === 0) this.loadUsers()
    },
    updateInvitedUser(emails) {
      let count = 0
      emails.forEach(email => {
        if (!this.invitedUsers.find(invited => invited.email === email)) {
          let invitedAt = new Date()
          this.invitedUsers.push({
            role: Roles.MEMBER,
            email,
            invitedAt,
            managedActors: []
          })
          count++
        }
      })
      this.showAlert(`You have invited ${count} users`, 'success')
    },
    updateRoleInvitation(email, newRole) {
      const user = this.invitedUsers.find(i => i.email === email)
      user.role = newRole
    },
    onError(error) {
      let userError = `(${error.devCode}) ${error.devMessage}`
      if (error.devCode === ErrorCodes.ACCESS_CONTROL) {
        console.log('==> TODO AC: call to action upgrade')
        userError = AlertMessages.ACCESS_CONTROL_ERROR.replace(
          '##action##',
          'invite new users'
        )
      }
      this.showAlert(userError)
    },
    onReSend(email) {
      this.showAlert(`You have re-sent an invitation to ${email}`, 'success')
    },
    onRevoke(email) {
      // Refresh list
      let index = this.invitedUsers.findIndex(user => user.email === email)
      this.invitedUsers.splice(index, 1)
      this.showAlert(`You have revoked the invitation to ${email}`, 'success')
    },
    onDeleteUser() {
      this.setUsers()
    },
    showAlert(msg, type = 'error') {
      this.alert.message = msg
      this.alert.type = type
      this.alert.show = true
      this.resetTimeout()
      this.timeout = setTimeout(() => this.resetAlert(), 8000)
    },
    resetAlert() {
      this.resetTimeout()
      this.alert.show = false
    },
    resetTimeout() {
      if (this.timeout) clearTimeout(this.timeout)
    }
  }
}
</script>

<style scoped>
.ModalUserList {
  .Search {
    margin-top: 0;
    margin-bottom: 20px;
  }
  .PendingUsers {
    margin-bottom: 40px;
  }
  .Users {
    &-header {
      display: flex;
      align-items: center;
      margin-bottom: 30px;
    }
    &-title {
      font-size: 14px;
      color: #969696;
    }
    &-list {
      margin: 0;
      padding-left: 0;
    }
    .SearchUsers {
      margin-left: auto;
    }
  }
  @media (--mobile) {
    .Modal-footer {
      padding: 20px;
    }
  }
}
</style>
<style>
.ModalUserList {
  .Modal-wrapper {
    width: 700px;
  }
}
</style>
